/**
 * Shared button shells for mobile page chrome (standardised mobile page layout):
 * the bordered square icon trigger (e.g. nav, filters, back), the bordered pill trigger
 * (e.g. filter selectors), and the primary (yellow) pill trigger. Call sites pass
 * layout classes (flex-1, w-full, justify-*) via className.
 *
 * Used by MobilePageHeader, mobile footers, card lists and filter sheets across
 * Sales/OrderManagement and EventManagement.
 */

import { type ComponentPropsWithoutRef } from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ICON_BUTTON_CLASS =
  'flex size-16 shrink-0 touch-manipulation items-center justify-center rounded-input border border-dinnerplate-white bg-off-white text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black/30';

const PILL_BUTTON_CLASS =
  'flex touch-manipulation items-center rounded-input border border-dinnerplate-white bg-off-white px-6 py-4 font-inter text-sm font-semibold leading-normal text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black/30';

export function MobileIconButton({ className, ...props }: ComponentPropsWithoutRef<'button'>) {
  return <button type="button" className={cn(ICON_BUTTON_CLASS, className)} {...props} />;
}

export function MobileIconLink({ className, ...props }: ComponentPropsWithoutRef<'a'>) {
  return <a className={cn(ICON_BUTTON_CLASS, className)} {...props} />;
}

export function MobilePillButton({ className, ...props }: ComponentPropsWithoutRef<'button'>) {
  return <button type="button" className={cn(PILL_BUTTON_CLASS, className)} {...props} />;
}

/** The shared primary variant, with the touch/focus treatment mobile chrome needs. */
export function MobilePrimaryButton({ className, ...props }: ButtonProps) {
  return (
    <Button
      className={cn('touch-manipulation px-6 focus-visible:ring-2 focus-visible:ring-off-black/30', className)}
      {...props}
    />
  );
}